import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { memo, useCallback, useEffect, useState } from 'react';
import type { Brand } from '@apiTypes';
import { Button, HStack, Text, VStack } from '@library';
import ProductList from '../ProductList/List/ProductList';
import useLoginState from '../../../core/hooks/useLoginState';
import useBrands from '../../../brand/queries/useBrands';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import BrandSearchMetadata, {
    BrandSearchResultsTextHeader,
} from '../BrandSearchMetadata';
import ProductSearchSidePanel from '../ProductSearchSidePanel';
import BrandsList from '../../../brand/components/BrandsList';
import useCurrentStore from '../../../core/hooks/useCurrentStore';
import { FluidProgressIndicator } from '../../../core/components/ProgressIndicators';
import BrandFilters from '../../../brand/components/BrandFilters';
import CheckmarkIcon from '../../../core/components/icons/CheckmarkIcon';
import useCategory from '../../../core/queries/useCategory';
import RestrictedContent from '../../../core/components/router/RestrictedContent';
import CustomLink from '../../../core/components/CustomLink';
import { useBrandSearchResultFilters } from '../../queries/useBrand';
import notEmpty from '../../../core/util/notEmpty';
import PageNotFound from '../../../core/components/router/PageNotFound';
import styles from './BrandSearchResults.module.css';

export const BrandSearchHeader = memo(({ brand }: { brand?: Brand }) => {
    const [search, setSearch] = useState('');
    const [startLetter, setStartLetter] = useState('');

    const { brands, loading, availableLetters } = useBrands({
        query: search,
        startLetter,
    });

    const isLoggedIn = useLoginState();
    const { brands: brandsWithKeyDealers } = useBrands({
        withKeyDealers: true,
        skip: isLoggedIn,
    });

    const {
        features: { customBrandDivisions },
    } = useCurrentStore();

    const [isSidePaneOpen, setIsSidePaneOpen] = useState(false);
    const [showModelUpdatedLabel, setShowModelUpdatedLabel] = useState(false);

    const toggleSidePane = () => {
        setIsSidePaneOpen(!isSidePaneOpen);
    };

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        if (showModelUpdatedLabel) {
            timer = setTimeout(() => {
                setShowModelUpdatedLabel(false);
            }, 5000);
        }
        return () => clearTimeout(timer);
    }, [showModelUpdatedLabel]);

    const handleModelChange = () => {
        setIsSidePaneOpen(false);
        setShowModelUpdatedLabel(true);
    };

    const handleLetterChange = useCallback((newLetter: string) => {
        setStartLetter(newLetter);
    }, []);

    const handleSearchChange = useCallback((newQuery: string) => {
        setSearch(newQuery);
    }, []);

    return (
        <div className={styles.brandSearchMetadataHeader}>
            <VStack gap="smallMedium">
                <div className={styles.brandSearchMetadata}>
                    <BrandSearchMetadata brand={brand} />

                    <HStack gap="small" align="center">
                        {showModelUpdatedLabel && (
                            <HStack gap="micro">
                                <CheckmarkIcon color="#647FB1" />
                                <Text component="h3" color="info">
                                    <FormattedMessage id="Brand BrandHeader Changed" />
                                </Text>
                            </HStack>
                        )}
                        <RestrictedContent
                            storeFlags="dealerSearchEnabled"
                            loggedOutOnly
                        >
                            {brandsWithKeyDealers.find(
                                item => item.name === brand?.name
                            ) ? (
                                <CustomLink
                                    to={`../../dealers?brands=${brand?.name}`}
                                >
                                    <Button>
                                        <FormattedMessage id="find dealers" />
                                    </Button>
                                </CustomLink>
                            ) : undefined}
                        </RestrictedContent>
                        <Button outline onClick={toggleSidePane}>
                            <FormattedMessage id="Brand BrandHeader ChangeBrand" />
                        </Button>
                    </HStack>
                </div>
            </VStack>

            <ProductSearchSidePanel
                isOpen={isSidePaneOpen}
                toggle={toggleSidePane}
            >
                {loading ? (
                    <VStack>
                        <FluidProgressIndicator />
                    </VStack>
                ) : (
                    <VStack gap="smallMedium">
                        <BrandFilters
                            query={search}
                            chosenLetter={startLetter}
                            onChangeQuery={handleSearchChange}
                            availableLetters={availableLetters}
                            onChangeLetter={handleLetterChange}
                            totalBrands={brands.length}
                        />
                        <BrandsList
                            brands={brands}
                            divisions={
                                customBrandDivisions?.enabled
                                    ? customBrandDivisions.divisions
                                    : []
                            }
                            onBrandSelect={handleModelChange}
                        />
                    </VStack>
                )}
            </ProductSearchSidePanel>
        </div>
    );
});

const BrandSearchResults = () => {
    const { brandSlug = '' } = useParams<{
        brandSlug: string;
    }>();

    const { categoryId, categoryName } = useCategory();
    const [searchParams] = useSearchParams();

    const { brand, loading, filters } = useBrandSearchResultFilters(brandSlug);

    const intl = useIntl();

    const title = intl.formatMessage(
        {
            id: 'Brand ProductListPage Title',
        },
        { brandName: brand?.name }
    );

    if (!loading && !notEmpty(brand)) {
        return <PageNotFound />;
    }

    return (
        <ProductList
            title={title}
            isFiltersLoading={loading}
            filters={filters}
            ignoreList={['PIM_ATT_ERP_MANUFACTURER']}
            topBanner={<BrandSearchHeader brand={brand} />}
            header={
                <BrandSearchResultsTextHeader
                    categoryName={categoryName}
                    selectedCategoryId={categoryId ?? ''}
                />
            }
            outerLoading={loading}
            rootLinks={[
                {
                    messageId: 'brands',
                    to: '/brands',
                },
                {
                    text: `${brand?.name}`,
                    to: `/brand/${brandSlug}?${searchParams.toString()}`,
                },
            ]}
            isSearchResultPage
        />
    );
};

export default memo(withErrorBoundary(BrandSearchResults));
